// src/services/api.js
export const BASE_URL = 'https://server.vastuanalyzer.com/api';
// export const BASE_URL = 'http://localhost:8000/api';

const NO_AUTH_ENDPOINTS = ['/user/login', '/admin/login', '/user/verify-login-otp'];

export const apiCall = async (endpoint, options = {}) => {
  try {
    const url = `${BASE_URL}${endpoint}`;
    const adminToken = localStorage.getItem('admin_token');
    const userToken = localStorage.getItem('user_token');
    const token = adminToken || userToken;
    const skipAuth = NO_AUTH_ENDPOINTS.includes(endpoint);

    const config = {
      headers: {
        'Accept': 'application/json',
        ...(!skipAuth && token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    if (!(options.body instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, config);
    
    if (!response.ok) {
      if (response.status === 401) {
        if (adminToken) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          window.location.href = '/admin/login';
        } else {
          localStorage.removeItem('user_token');
          localStorage.removeItem('user_data');
          window.location.href = '/login';
        }
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// Admin Auth API
export const adminAuthAPI = {
  login: async (email, password) => {
    return await apiCall('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  logout: async () => {
    return await apiCall('/admin/logout', {
      method: 'POST',
    });
  },

  getCurrentUser: async () => {
    return await apiCall('/admin/me', {
      method: 'GET',
    });
  }
};

// User Auth API (Regular Users)
export const userAuthAPI = {
  login: async (email, password) => {
    return await apiCall('/user/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  logout: async () => {
    return await apiCall('/user/logout', {
      method: 'POST',
    });
  },

  getCurrentUser: async () => {
    return await apiCall('/user/me', {
      method: 'GET',
    });
  },

  verifyLoginOtp: async (email, otp) => {
    return await apiCall('/user/verify-login-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    });
  },

  forgotPassword: async (email) => {
    return await apiCall('/user/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  resetPassword: async (email, otp, new_password) => {
    return await apiCall('/user/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email, otp, new_password }),
    });
  },

  changePassword: async (current_password, new_password) => {
    return await apiCall('/user/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password, new_password }),
    });
  },

  updateProfile: async (profileData) => {
    return await apiCall('/user/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  },

  uploadLogo: async (file) => {
    const fd = new FormData();
    fd.append('logo', file);
    return await apiCall('/user/profile/logo', {
      method: 'POST',
      body: fd,
    });
  },

  /**
   * Fetch an uploaded company logo as a base64 data-URL instead of loading the raw
   * /api/user-logo/{filename} URL directly. The CDN in front of the API intercepts
   * any URL whose *path* ends in an image extension (.png etc) as a static asset and
   * never forwards it to Laravel — no CORS headers, so the editor canvas's
   * crossOrigin="anonymous" load fails and export throws a "tainted canvas" error.
   * Routing the bytes through JSON (filename as a query param) sidesteps that.
   * Falls back to the raw URL if this fails, or if it's some other external URL.
   */
  resolveLogoImage: async (logoUrl) => {
    if (!logoUrl || !logoUrl.includes('/api/user-logo/')) return logoUrl;
    try {
      const filename = logoUrl.split('/api/user-logo/')[1];
      const response  = await apiCall(`/user/logo-data?filename=${encodeURIComponent(filename)}`, { method: 'GET' });
      return response?.data || logoUrl;
    } catch {
      return logoUrl;
    }
  },

  /**
   * Fire-and-forget: bumps the logged-in user's export_count by 1 on the
   * backend (shown as its own column in the admin Users table — total
   * exports across ALL of that user's projects, not per-project). Call
   * this right after a PDF export actually finishes (doc.save()).
   * Never throws — a failed count-bump must never block or error out the
   * PDF the user just successfully downloaded.
   */
  recordExport: async () => {
    // Only a logged-in "user" account has export_count on their row — skip
    // silently for an admin session (no user_token) rather than risk
    // hitting this endpoint with an admin token.
    if (!localStorage.getItem('user_token')) return;
    try {
      await apiCall('/user/record-export', { method: 'POST' });
    } catch {
      // silent — export tracking is best-effort, not user-facing
    }
  },
};

// User Management API (Admin only)
export const userAPI = {
  getAll: async () => {
    return await apiCall('/admin/users', {
      method: 'GET',
    });
  },

  getById: async (id) => {
    return await apiCall(`/admin/users/${id}`, {
      method: 'GET',
    });
  },

  create: async (userData) => {
    return await apiCall('/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  update: async (id, userData) => {
    return await apiCall(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  },

  delete: async (id) => {
    return await apiCall(`/admin/users/${id}`, {
      method: 'DELETE',
    });
  },

  resetPassword: async (id, password) => {
    return await apiCall(`/admin/users/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  },

  updateStatus: async (id, status) => {
    return await apiCall(`/admin/users/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }
};

export const projectAPI = {
  create: async (formData) => {
    return await apiCall('/projects', {
      method: 'POST',
      body: formData,
    });
  },

  getById: async (id) => {
    return await apiCall(`/projects/${id}`, {
      method: 'GET',
    });
  }
};

const api = {
  adminAuth: adminAuthAPI,
  userAuth: userAuthAPI,
  user: userAPI,
  project: projectAPI,
};

export default api;